/**
 * Cloudflare Image Resizing URL utility.
 *
 * All media is hosted on R2. Constructs a resize URL so Cloudflare
 * serves an optimized version at the edge.
 */

import { CDN_BASE_URL } from "@/config/constants";

/** Grid thumbnail sizing — used by all gallery/grid displays. */
export const GRID_THUMBNAIL_WIDTH = 400;
export const GRID_THUMBNAIL_QUALITY = 80;

/** Extensions that Cloudflare Image Resizing cannot process. */
const NON_IMAGE_EXTENSIONS = [".mp4", ".webm", ".mov"];

/**
 * Build a Cloudflare Image Resizing URL.
 *
 * Returns `{base}/cdn-cgi/image/width={w},quality={q},format=auto/{path}`
 * Video files are returned as-is (Cloudflare cannot resize them).
 */
export function getResizedImageUrl(
  url: string,
  width: number,
  options?: { quality?: number },
): string {
  if (!url) return "";
  const lower = url.toLowerCase();
  if (NON_IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext))) return url;
  if (!import.meta.env.PROD) return url;

  if (!CDN_BASE_URL || !url.startsWith(CDN_BASE_URL)) return url;

  const quality = options?.quality ?? GRID_THUMBNAIL_QUALITY;
  const path = url.slice(CDN_BASE_URL.length + 1); // Remove base URL + leading slash
  return `${CDN_BASE_URL}/cdn-cgi/image/width=${width},quality=${quality},format=auto/${path}`;
}
